import React from 'react';
import { Link } from 'react-router';
import { CreditCard } from 'lucide-react';
import type { LscBillingStatusResponse } from '../types';
import { billingCadenceLabel, isCompedEntitlement, LSC_SUBSCRIPTION_PRODUCT_NAME } from '../billingCopy';
import { lscPath } from '../routing';

interface LeagueEntitlementSummaryProps {
  leagueId: string;
  billingStatus: LscBillingStatusResponse | null;
  loading?: boolean;
}

function describeEntitlement(billingStatus: LscBillingStatusResponse | null): {
  headline: string;
  detail: string;
  tone: 'success' | 'warning' | 'muted';
} {
  if (!billingStatus?.billing_model_enabled) {
    return {
      headline: 'Subscription billing unavailable',
      detail: 'Subscription billing is not enabled in this environment.',
      tone: 'muted',
    };
  }

  const entitlement = billingStatus.entitlement;
  if (entitlement && isCompedEntitlement(entitlement)) {
    return {
      headline: 'Complimentary access',
      detail: 'This league has complimentary access — not a paid subscription.',
      tone: 'success',
    };
  }

  if (entitlement) {
    const cadence = entitlement.billing_cadence
      ? billingCadenceLabel(entitlement.billing_cadence)
      : null;
    return {
      headline: `${LSC_SUBSCRIPTION_PRODUCT_NAME} linked to this league`,
      detail: cadence ? `Active subscription · ${cadence}` : 'Active subscription for this league.',
      tone: 'success',
    };
  }

  const subStatus = (billingStatus.subscription_status || billingStatus.status || '')
    .toLowerCase()
    .trim();
  if (subStatus === 'active' || subStatus === 'trialing') {
    return {
      headline: 'Subscription active — league not linked',
      detail:
        'Your account has an active subscription, but this league is not linked yet.',
      tone: 'warning',
    };
  }

  return {
    headline: 'No active subscription for this league',
    detail: 'Subscribe or link subscription access before league automation can run.',
    tone: 'warning',
  };
}

const toneClasses = {
  success: 'border-emerald-700/50 bg-emerald-950/20',
  warning: 'border-amber-700/50 bg-amber-950/20',
  muted: 'border-secondary-700 bg-secondary-900/40',
};

const LeagueEntitlementSummary: React.FC<LeagueEntitlementSummaryProps> = ({
  leagueId,
  billingStatus,
  loading = false,
}) => {
  const copy = describeEntitlement(billingStatus);

  return (
    <div
      className={`rounded-lg border px-4 py-3 space-y-2 ${toneClasses[copy.tone]}`}
      data-testid="league-entitlement-summary"
    >
      <div className="flex items-start gap-3">
        <CreditCard className="h-5 w-5 text-primary-400 shrink-0 mt-0.5" aria-hidden />
        <div className="min-w-0 space-y-1">
          <h3 className="text-sm font-semibold text-white">League subscription</h3>
          <p className="text-sm text-white">{loading ? 'Loading subscription…' : copy.headline}</p>
          {!loading && <p className="text-xs text-secondary-400">{copy.detail}</p>}
        </div>
      </div>
      <div className="flex flex-wrap gap-3 pl-8">
        <Link
          to={lscPath(`/leagues/${leagueId}/settings`)}
          className="text-xs font-medium text-primary-300 hover:text-primary-200"
        >
          League settings
        </Link>
        <Link
          to={lscPath('/billing')}
          className="text-xs font-medium text-primary-300 hover:text-primary-200"
        >
          Account billing
        </Link>
      </div>
    </div>
  );
};

export default LeagueEntitlementSummary;
